/**
 * Reads a rendered page as a tree of what a user can see and use: headings,
 * tabs, internal links and where they go, buttons, form fields, open dialogs
 * and menus, and the data-component name of every region that holds any of
 * them. Plain paragraphs are left out; the model reads data through the API.
 *
 * One reader, two uses:
 * - the assistant's live outline (assistant-view-capture.tsx), with the user's
 *   data: field values (never a secret one) and the record ids of regions;
 * - the screen map the Playwright crawl writes (e2e/helpers/screen-map/), with
 *   `data: false`: the same structure without values or record ids. The crawl
 *   injects this module into the page as plain JavaScript, so it must import
 *   nothing.
 */

export type OutlineKind =
	| "page"
	| "heading"
	| "tabs"
	| "tab"
	| "dialog"
	| "menu"
	| "region"
	| "button"
	| "item"
	| "link"
	| "field";

export interface OutlineNode {
	kind: OutlineKind;
	/** The heading, button or field label; the page path; a dialog's title. */
	label: string;
	/** A heading's level. */
	level?: string;
	/** Where a tab, link or menu item goes. */
	href?: string;
	/** The MUI icon a named button shows (`MoreVert` for ⋮): what the user sees. */
	icon?: string;
	/** The document title, on the page node. */
	title?: string;
	component?: string;
	/** A field's input type (text, checkbox, textarea, combobox, ...). */
	fieldType?: string;
	/** A field's current value, or a region's data-*-id attributes (data mode only). */
	value?: string;
	selected?: boolean;
	disabled?: boolean;
	/** The tag the reader set on a button or tab, when asked to (`tagAttribute`). */
	tag?: string;
	children: OutlineNode[];
}

export interface OutlineOptions {
	/** Read field values and record ids (the live outline), or leave them out (the map). */
	data: boolean;
	/** When set, each button, menu item and tab gets this attribute with its tag. */
	tagAttribute?: string;
}

export interface PageOutline {
	text: string;
	/** True when the outline was cut at the character limit. */
	truncated: boolean;
}

const SKIPPED_TAGS = new Set([
	"SCRIPT",
	"STYLE",
	"NOSCRIPT",
	"SVG",
	"TEMPLATE",
	"IFRAME",
]);
/**
 * A date or time picker's grid (the month shown, a button per day, the clock
 * face): today's calendar, not something on the page to describe.
 */
const SKIPPED_SELECTOR =
	'[class*="MuiDateCalendar-root"], [class*="MuiPickersLayout-contentWrapper"], [class*="MuiClock-root"], [class*="DigitalClock-root"]';
const LABEL_MAX_CHARS = 80;
const VALUE_MAX_CHARS = 80;
const INDENT = "  ";
/** A field whose name or label says it holds a credential shows no value. */
const SECRET_FIELD = /pass(word|wd)?|secret|token|api[-_ ]?key|otp|pin\b/i;
const BUTTON_INPUT_TYPES = new Set(["button", "submit", "reset"]);
const FIELD_ROLES = new Set([
	"combobox",
	"textbox",
	"searchbox",
	"switch",
	"checkbox",
	"radio",
	"slider",
]);
const ITEM_ROLES = new Set(["menuitem", "menuitemcheckbox", "menuitemradio", "option"]);
/** Kinds that hold others, and are kept only if something inside them is. */
const CONTAINERS = new Set<OutlineKind>(["page", "tabs", "dialog", "menu", "region"]);

const squash = (text: string | null | undefined, max: number): string => {
	const flat = (text ?? "").replace(/\s+/g, " ").trim();
	return flat.length > max ? `${flat.slice(0, max - 1)}…` : flat;
};

const quote = (text: string): string => `"${text.replaceAll('"', "'")}"`;

/**
 * An element's text as laid out: a card's title and the path under it stay
 * two words ("Analytics api/analytics"), where textContent glues them
 * ("Analyticsapi/analytics").
 */
const textOf = (element: Element | null | undefined): string | null | undefined =>
	element instanceof HTMLElement
		? element.innerText || element.textContent
		: element?.textContent;

const isHidden = (element: Element): boolean => {
	if (element.hasAttribute("hidden")) return true;
	const style = getComputedStyle(element);
	return style.display === "none" || style.visibility === "hidden";
};

const labelledBy = (element: Element): string => {
	const ids = (element.getAttribute("aria-labelledby") ?? "")
		.split(/\s+/)
		.filter((id) => id && id !== element.id);
	return ids
		.map((id) => squash(textOf(document.getElementById(id)), LABEL_MAX_CHARS))
		.join(" ");
};

/** The caption of <input type=button|submit|reset>. */
const buttonInputCaption = (element: Element): string =>
	element instanceof HTMLInputElement && BUTTON_INPUT_TYPES.has(element.type)
		? element.value
		: "";

/** The MUI icon an icon-only button shows: `icon:Delete`. */
const iconName = (element: Element): string => {
	const id = element.querySelector("svg[data-testid]")?.getAttribute("data-testid");
	return id?.endsWith("Icon") ? `icon:${id.slice(0, -4)}` : "";
};

/**
 * What a user would call this element: its accessible name (MUI's Tooltip
 * sets aria-label on the button it wraps), else its text, else its title;
 * an icon button with none of those is named by the icon the user sees, and
 * only one with no icon either by its data-component (a name users never see).
 */
const nameOf = (element: Element): string =>
	squash(
		element.getAttribute("aria-label") ||
			labelledBy(element) ||
			buttonInputCaption(element) ||
			textOf(element) ||
			element.getAttribute("title") ||
			iconName(element) ||
			element.getAttribute("data-component"),
		LABEL_MAX_CHARS
	);

/** data-*-id attributes name the record a region shows (a focus selector uses them). */
const recordIds = (element: Element): string =>
	element
		.getAttributeNames()
		.filter((name) => /^data-.+-id$/.test(name))
		.slice(0, 3)
		.map((name) => `${name}=${element.getAttribute(name)}`)
		.join(" ");

const internalPath = (anchor: HTMLAnchorElement): string | null => {
	const href = anchor.getAttribute("href");
	if (!href || href.startsWith("#") || href.startsWith("//")) return null;
	if (href.startsWith("/")) return href;
	const url = new URL(anchor.href, location.href);
	return url.origin === location.origin ? `${url.pathname}${url.search}` : null;
};

const isDisabled = (element: Element): boolean =>
	element.hasAttribute("disabled") ||
	element.getAttribute("aria-disabled") === "true";

const componentOf = (element: Element): string | undefined =>
	element.getAttribute("data-component") ?? undefined;

const node = (
	kind: OutlineKind,
	label: string,
	extra: Partial<OutlineNode> = {}
): OutlineNode => ({ kind, label, children: [], ...extra });

const fieldLabel = (element: Element): string => {
	const own = element.getAttribute("aria-label") || labelledBy(element);
	if (own) return squash(own, LABEL_MAX_CHARS);
	if (
		element instanceof HTMLInputElement ||
		element instanceof HTMLTextAreaElement ||
		element instanceof HTMLSelectElement
	) {
		const fromLabels = squash(
			Array.from(element.labels ?? [])
				.map((label) => textOf(label))
				.join(" "),
			LABEL_MAX_CHARS
		);
		if (fromLabels) return fromLabels;
		if (!(element instanceof HTMLSelectElement) && element.placeholder) {
			return squash(element.placeholder, LABEL_MAX_CHARS);
		}
		return element.name;
	}
	return "";
};

const fieldValue = (element: Element): string => {
	if (element instanceof HTMLInputElement) {
		if (element.type === "checkbox" || element.type === "radio") {
			return element.checked ? "checked" : "unchecked";
		}
		return element.value ? quote(squash(element.value, VALUE_MAX_CHARS)) : "empty";
	}
	if (element instanceof HTMLTextAreaElement) {
		return element.value ? quote(squash(element.value, VALUE_MAX_CHARS)) : "empty";
	}
	if (element instanceof HTMLSelectElement) {
		return quote(squash(element.selectedOptions[0]?.textContent, VALUE_MAX_CHARS));
	}
	const checked = element.getAttribute("aria-checked");
	if (checked) return checked === "true" ? "checked" : "unchecked";
	const text = squash(element.textContent, VALUE_MAX_CHARS);
	return text ? quote(text) : "empty";
};

const isNativeField = (element: Element): boolean =>
	(element instanceof HTMLInputElement &&
		!BUTTON_INPUT_TYPES.has(element.type) &&
		element.type !== "hidden") ||
	element instanceof HTMLTextAreaElement ||
	element instanceof HTMLSelectElement;

/** MUI draws a Select as a role=combobox div plus a hidden twin input. */
const isTwinInput = (element: Element): boolean =>
	element.getAttribute("aria-hidden") === "true" &&
	(element as HTMLElement).tabIndex === -1;

const fieldNode = (element: Element, options: OutlineOptions): OutlineNode | null => {
	if (element instanceof HTMLInputElement && element.type === "password") {
		return null;
	}
	if (isNativeField(element) && isTwinInput(element)) return null;
	const label = fieldLabel(element);
	const secret = SECRET_FIELD.test(`${label} ${element.getAttribute("name") ?? ""}`);
	const fieldType =
		element instanceof HTMLInputElement
			? element.type
			: (element.getAttribute("role") ?? element.tagName.toLowerCase());
	return node("field", label || "(no label)", {
		fieldType,
		component: componentOf(element),
		disabled: element.hasAttribute("disabled"),
		value: options.data ? (secret ? "(hidden)" : fieldValue(element)) : undefined,
	});
};

const dialogTitle = (element: Element): string =>
	squash(
		element.getAttribute("aria-label") ||
			labelledBy(element) ||
			element.querySelector("h1, h2, h3, h4, h5, h6, [role=heading]")
				?.textContent,
		LABEL_MAX_CHARS
	);

const isDialog = (element: Element): boolean =>
	element.getAttribute("role") === "dialog" ||
	element.getAttribute("role") === "alertdialog" ||
	(element instanceof HTMLDialogElement && element.open);

/** The node an element contributes, and whether its children are read too. */
const classify = (
	element: Element,
	options: OutlineOptions
): { found: OutlineNode | null; descend: boolean } => {
	const role = element.getAttribute("role");
	if (/^H[1-6]$/.test(element.tagName) || role === "heading") {
		const text = squash(textOf(element), LABEL_MAX_CHARS);
		const level =
			/^H([1-6])$/.exec(element.tagName)?.[1] ??
			element.getAttribute("aria-level") ??
			"";
		return {
			found: text ? node("heading", text, { level }) : null,
			descend: false,
		};
	}
	if (role === "tab") {
		return {
			found: node("tab", nameOf(element), {
				// Resolved like a link's: a LinkTab's href is only a query
				// ("?tab=description"), which means this page, not "/".
				href:
					element instanceof HTMLAnchorElement
						? (internalPath(element) ?? undefined)
						: undefined,
				component: componentOf(element),
				selected: element.getAttribute("aria-selected") === "true",
				disabled: isDisabled(element),
			}),
			descend: false,
		};
	}
	if (role === "tablist") return { found: node("tabs", ""), descend: true };
	if (isDialog(element)) {
		return {
			found: node("dialog", dialogTitle(element), {
				component: componentOf(element),
			}),
			descend: true,
		};
	}
	if (role === "menu" || role === "listbox") {
		return {
			found: node("menu", "", { component: componentOf(element) }),
			descend: true,
		};
	}
	if (role !== null && ITEM_ROLES.has(role)) {
		// A menu item that is a link (MUI's MenuItem component={Link}) says where it goes.
		const href =
			element instanceof HTMLAnchorElement ? internalPath(element) : null;
		return {
			found: node("item", nameOf(element), {
				href: href ?? undefined,
				component: componentOf(element),
				disabled: isDisabled(element),
			}),
			descend: false,
		};
	}
	if (
		element.tagName === "BUTTON" ||
		role === "button" ||
		(element instanceof HTMLInputElement && BUTTON_INPUT_TYPES.has(element.type))
	) {
		const name = nameOf(element) || "(no label)";
		const icon = iconName(element);
		return {
			found: node("button", name, {
				// A named icon button ("show more") is seen as its icon (⋮).
				icon: icon && icon !== name ? icon.slice("icon:".length) : undefined,
				component: componentOf(element),
				disabled: isDisabled(element),
			}),
			descend: false,
		};
	}
	if (element instanceof HTMLAnchorElement && element.hasAttribute("href")) {
		const path = internalPath(element);
		return {
			found:
				path === null
					? null
					: node("link", nameOf(element) || "(no label)", {
							href: path,
							component: componentOf(element),
						}),
			descend: false,
		};
	}
	if (isNativeField(element) || (role !== null && FIELD_ROLES.has(role))) {
		return { found: fieldNode(element, options), descend: false };
	}
	if (element.hasAttribute("data-component")) {
		return {
			found: node("region", "", {
				component: componentOf(element),
				value: options.data ? recordIds(element) || undefined : undefined,
			}),
			descend: true,
		};
	}
	return { found: null, descend: true };
};

const sameLeaf = (a: OutlineNode, b: OutlineNode): boolean =>
	!CONTAINERS.has(a.kind) &&
	a.kind === b.kind &&
	a.label === b.label &&
	a.href === b.href &&
	a.component === b.component &&
	a.value === b.value;

const walk = (
	element: Element,
	parent: OutlineNode,
	options: OutlineOptions,
	counter: { next: number }
): void => {
	if (
		SKIPPED_TAGS.has(element.tagName.toUpperCase()) ||
		element.matches(SKIPPED_SELECTOR) ||
		isHidden(element)
	) {
		return;
	}
	const { found, descend } = classify(element, options);
	let into = parent;
	if (found) {
		if (!parent.children.some((child) => sameLeaf(child, found))) {
			if (
				options.tagAttribute &&
				(found.kind === "button" ||
					found.kind === "tab" ||
					found.kind === "item")
			) {
				found.tag = String(counter.next++);
				element.setAttribute(options.tagAttribute, found.tag);
			}
			parent.children.push(found);
		}
		into = found;
	}
	if (!descend) return;
	for (const child of Array.from(element.children))
		walk(child, into, options, counter);
};

const hasContent = (outline: OutlineNode): boolean =>
	!CONTAINERS.has(outline.kind) || outline.children.some(hasContent);

/** Drop containers with nothing in them, the same rule `render` applies. */
const pruned = (outline: OutlineNode): OutlineNode => ({
	...outline,
	children: outline.children.filter(hasContent).map(pruned),
});

/** The tree of `root` (a region, a dialog or the whole page). */
export const readOutline = (root: Element, options: OutlineOptions): OutlineNode => {
	const top = node("page", location.pathname, {
		title: document.title || undefined,
	});
	walk(root, top, options, { next: 0 });
	return pruned(top);
};

const suffix = (outline: OutlineNode): string =>
	`${outline.component ? ` [${outline.component}]` : ""}${outline.disabled ? " disabled" : ""}`;

/** One node as a line of the outline text. */
export const outlineLine = (outline: OutlineNode): string => {
	switch (outline.kind) {
		case "page":
			return `page ${outline.label}${outline.title ? ` ${quote(outline.title)}` : ""}`;
		case "heading":
			return `h${outline.level ?? ""} ${quote(outline.label)}`;
		case "tabs":
			return "tabs";
		case "tab":
			return `tab ${quote(outline.label)}${outline.selected ? " selected" : ""}${outline.disabled ? " disabled" : ""}${outline.href ? ` -> ${outline.href}` : ""}${outline.component ? ` [${outline.component}]` : ""}`;
		case "dialog":
			return `dialog${outline.label ? ` ${quote(outline.label)}` : ""}${suffix(outline)}`;
		case "menu":
			return `menu${suffix(outline)}`;
		case "region":
			return `[${outline.component}]${outline.value ? ` ${outline.value}` : ""}`;
		case "button":
			return `button ${quote(outline.label)}${outline.icon ? ` icon:${outline.icon}` : ""}${suffix(outline)}`;
		case "item":
			return `item ${quote(outline.label)}${outline.href ? ` -> ${outline.href}` : ""}${suffix(outline)}`;
		case "link":
			return `link ${quote(outline.label)} -> ${outline.href}${outline.component ? ` [${outline.component}]` : ""}`;
		case "field":
			return `field ${quote(outline.label)} ${outline.fieldType}${suffix(outline)}${outline.value === undefined ? "" : ` = ${outline.value}`}`;
	}
};

/** The tree as indented lines. */
export const outlineLines = (outline: OutlineNode, depth = 0): string[] => [
	`${INDENT.repeat(depth)}${outlineLine(outline)}`,
	...outline.children.flatMap((child) => outlineLines(child, depth + 1)),
];

/** Cut at a line boundary so the model never reads half a line. */
const cut = (lines: string[], maxChars: number): PageOutline => {
	const kept: string[] = [];
	let size = 0;
	for (const line of lines) {
		if (size + line.length + 1 > maxChars) {
			return { text: kept.join("\n"), truncated: true };
		}
		kept.push(line);
		size += line.length + 1;
	}
	return { text: kept.join("\n"), truncated: false };
};

/** The live outline of `root`, with the user's data, at most `maxChars` long. */
export const outlinePage = (root: Element, maxChars: number): PageOutline =>
	cut(outlineLines(readOutline(root, { data: true })), maxChars);
